/**
 * StudentStartingBlock — Sam, en plein écran, juste avant d'entrer (`<StudentStartingBlockPage>`).
 *
 * La conversation est au moteur (`useCheckpointConversation`) ; ici, ce que seul l'écran sait faire :
 * ouvrir l'appareil photo quand une réponse le demande (`useMediaPickerAdapter`, le seul accès des
 * ports à la caméra) et rendre la photo, tenir la date choisie, et entrer dans le live au bout :
 * « C'est parti », l'avis donné, ou la croix.
 *
 * Quand Sam lance la génération d'une activité (« quiz d'échauffement »), la promesse est retenue avec
 * sa conversation et les activités à faire du moment (`rememberGeneration`) : le live ouvrira celle que
 * le serveur rattache à la conversation, même si elle a été poussée avant que l'élève soit dans la salle.
 * Quand Sam lance une activité de la liste (celle qui répond au besoin, ou le plan du masteur), elle devient
 * la suggestion de la séance (`setSuggestedActivity`) et une promesse connue par son id (`rememberLaunch`) :
 * c'est elle que le live ouvre, même arrivée dans la liste après sa dernière lecture.
 */
import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { formIds } from "../../../../../../data/constants/formConstants"
import { setSuggestedActivity } from "../../../../../../data/redux/actions/liveMode"
import { StudentStartingBlockPage } from "../../../../../../design-system/student"
import { type ApiCheckpointConversation } from "../../../../../../engine/precall/checkpoint/checkpointConversationMachine"
import {
    ACTIVITY_GENERATION_STATUS,
    launchedTodoId,
    rememberGeneration,
    rememberLaunch,
    type GeneratedTodoCandidate,
} from "../../../../../../engine/precall/checkpoint/generatedActivity"
import { useCheckpointConversation } from "../../../../../../engine/precall/checkpoint/useCheckpointConversation"
import { startingBlockViewModel } from "../../../../view-models/student/LiveEntry/startingBlockViewModel"
import { useMediaPickerAdapter } from "../../../hooks/useMediaPickerAdapter"
import { playStudentSound, SAM_SOUNDS } from "../studentSounds"
import { startingBlockGo } from "./liveEntryTracking"
import { selectDisplayedStudent } from "../../../../view-models/shared/displayedStudent"

/** Avant le live, il n'y a pas d'activité à reprendre : Sam dit d'entrer. Formulation à valider. */
const ERROR_BEFORE_LIVE = "Oups, un souci est survenu de notre côté. Ferme cette fenêtre : ton live t'attend."

type RootState = { data?: { currentUser?: { object?: { todo_activities?: GeneratedTodoCandidate[] | null } | null } } }

type Props = {
    conversation: ApiCheckpointConversation
    /** La séance où l'élève entre : l'activité que Sam fait générer s'y ouvrira. */
    liveSessionId: string
    /** Entrer dans le live : la conversation a fini son travail, ou l'élève l'a fermée. */
    onEnter: () => void
}

export function StudentStartingBlock({ conversation, liveSessionId, onEnter }: Props) {
    const dispatch = useDispatch()
    const todos = useSelector((state: RootState) => selectDisplayedStudent(state)?.todo_activities ?? null)
    const todosRef = useRef(todos)
    todosRef.current = todos
    const checkpoint = useCheckpointConversation({
        conversation,
        role: "student",
        surface: "screen",
        // Avant d'entrer, hors de l'appel.
        onSound: (sound) => playStudentSound(SAM_SOUNDS[sound], false, sound),
        onTurn: (data) => {
            const generating = data?.conversation
            if (generating?.status === ACTIVITY_GENERATION_STATUS && generating.id != null)
                rememberGeneration(liveSessionId, generating.id, todosRef.current ?? [])
            const launched = launchedTodoId(data)
            if (launched != null) {
                dispatch(setSuggestedActivity(liveSessionId, launched))
                rememberLaunch(liveSessionId, launched)
            }
        },
        onFinished: onEnter,
        onClosed: onEnter,
    })
    const vm = startingBlockViewModel(checkpoint.state)
    const [date, setDate] = useState("")

    // La photo : l'appareil s'ouvre, la réponse reste disponible si l'élève revient sans photo.
    const camera = useMediaPickerAdapter({ formId: formIds.CHECKPOINT, enableGallery: false, enableDocument: false })
    const awaitingPhotoRef = useRef(false)
    useEffect(() => {
        if (!checkpoint.photoRequested) return
        awaitingPhotoRef.current = true
        camera.launchCamera?.()
        checkpoint.cancelGesture()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkpoint.photoRequested])
    useEffect(() => {
        const [photo] = camera.incomingCameraImages
        if (!awaitingPhotoRef.current || photo == null) return
        awaitingPhotoRef.current = false
        camera.onCameraImagesConsumed()
        checkpoint.sendPhoto(photo.uri)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [camera.incomingCameraImages])

    return (
        <StudentStartingBlockPage
            days={vm.days}
            thinking={vm.thinking}
            replies={vm.replies}
            onReply={checkpoint.reply}
            chosenReplyKey={vm.chosenReplyKey}
            textEntry={
                vm.writing ? { value: vm.textValue, onChange: checkpoint.setText, onSend: checkpoint.sendText } : null
            }
            dateEntry={
                checkpoint.dateRequested
                    ? { value: date, onChange: setDate, onSend: () => checkpoint.sendDate(date) }
                    : null
            }
            answered={vm.answered}
            earned={vm.earned}
            canClose={checkpoint.canClose}
            onClose={checkpoint.close}
            ended={vm.ended}
            feedback={vm.feedback}
            onFeedback={(value) => checkpoint.giveFeedback(value === "positive" ? 1 : -1)}
            feedbackSubmitting={vm.feedbackSubmitting}
            endAction={{
                label: "C'est parti !",
                onPress: () => {
                    // Le clic de l'ancien bouton (`PreCallScreen`), celui d'une réponse touchée.
                    playStudentSound(SAM_SOUNDS.select, false, "select")
                    startingBlockGo(checkpoint.close)
                },
            }}
            error={vm.error}
            errorText={ERROR_BEFORE_LIVE}
        />
    )
}
